import { useRef, useState } from 'react';
import { Download, Hourglass, Trash2 } from 'lucide-react';
import { nivelCaducidad, PORTAL_INTEGRADORES_FACE } from '@shared/certificados';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResumenCertificado } from '@/components/ResumenCertificado';
import { AccionActivar } from '@/components/certificados/AccionActivar';
import { describirComprobacion } from '@/utils/comprobacionFace';
import { eliminarCertificadoApi } from '@/utils/certificadosApi';
import { formatCertificateSubject } from '@/utils/certificadosService';
import type { DigitalCertificate } from '@/types';

interface Props {
  certificado: DigitalCertificate;
  /** Entorno de FACe en el que se comprobará; `null` si el usuario no tiene FACe habilitado. */
  entornoFace: 'pruebas' | 'produccion' | null;
  onDescargar: (certificado: DigitalCertificate) => void;
  /** Se llama tras activar o eliminar: el padre recarga la lista y avisa a la app. */
  onCambio: () => void;
}

/** Tarjeta «Certificado preparado»: descargar el .pem, darlo de alta en FACe y activarlo. */
export function TarjetaCertificadoPreparado({ certificado, entornoFace, onDescargar, onCambio }: Props) {
  const { fetchWithAuth } = useUser();
  const { nivel, diasRestantes } = nivelCaducidad(certificado.validUntil);

  const [eliminando, setEliminando] = useState(false);
  const eliminandoRef = useRef(false);

  const eliminar = async () => {
    if (eliminandoRef.current) return;
    if (!window.confirm(`¿Eliminar el certificado «${certificado.nombre}»?`)) return;
    eliminandoRef.current = true;
    setEliminando(true);
    let eliminado = false;
    try {
      const resultado = await eliminarCertificadoApi(fetchWithAuth, certificado.id);
      if (resultado.ok) {
        toast.success('Certificado eliminado.');
        eliminado = true;
      } else {
        toast.error(resultado.error);
      }
    } catch {
      toast.error('No se pudo eliminar el certificado.');
    } finally {
      eliminandoRef.current = false;
      setEliminando(false);
    }
    // Fuera del try: si el padre falla al recargar, el borrado ya se hizo y no hay que decir lo contrario.
    if (eliminado) {
      try {
        onCambio();
      } catch (error) {
        console.error('Error al recargar tras eliminar el certificado:', error);
      }
    }
  };

  // Un entorno que este cliente aún no conoce (servidor más nuevo) no tiene URL: se nombra el portal sin enlace.
  const urlPortal: string | undefined = entornoFace ? PORTAL_INTEGRADORES_FACE[entornoFace] : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hourglass className="h-5 w-5 text-blue-600" aria-hidden="true" />
          Certificado preparado
        </CardTitle>
        <CardDescription>
          {entornoFace ?
            `Se comprobará con FACe en el entorno de ${entornoFace === 'pruebas' ? 'pruebas' : 'producción'} antes de activarlo.` :
            'No tienes FACe habilitado: se activará sin comprobar con FACe.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-lg font-semibold">{certificado.nombre}</p>
          <p className="text-sm text-muted-foreground">
            {certificado.subject ? formatCertificateSubject(certificado.subject) : 'titular desconocido'} ·{' '}
            {certificado.nif ? `NIF ${certificado.nif}` : 'sin NIF en el certificado'}
          </p>
          <p className="text-sm">
            <ResumenCertificado
              mostrarNombre={false}
              certificado={{ nombre: certificado.nombre, validUntil: certificado.validUntil, nivel, diasRestantes }}
            />
          </p>
          {certificado.comprobacionFace && (
            <p className="text-sm text-muted-foreground">{describirComprobacion(certificado.comprobacionFace)}</p>
          )}
        </div>

        <ol className="list-decimal space-y-4 pl-5">
          <li className="space-y-2">
            <p>Descarga la parte pública del certificado.</p>
            <Button variant="outline" size="sm" onClick={() => onDescargar(certificado)}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Descargar el .pem
            </Button>
          </li>
          {entornoFace && (
          <li className="space-y-1">
            <p>
              Dalo de alta en FACe. En el{' '}
              {urlPortal ? (
                <a
                  href={urlPortal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  portal de integradores
                </a>
              ) : (
                'portal de integradores'
              )}
              : Certificados → Alta, subiendo el .pem. Después, Plataformas → SantaFactura → Editar certificado → Agregar.
            </p>
          </li>
          )}
          <li className="space-y-2">
            <p>{entornoFace ? 'Cuando esté dado de alta, comprueba y activa el certificado.' : 'Activa el certificado.'}</p>
            <AccionActivar certificadoId={certificado.id} etiqueta="Comprobar y activar" onActivado={onCambio} />
          </li>
        </ol>

        <Button variant="ghost" size="sm" onClick={eliminar} aria-label={`Eliminar ${certificado.nombre}`} disabled={eliminando}>
          <Trash2 className="mr-2 h-4 w-4 text-destructive" aria-hidden="true" />
          Eliminar
        </Button>
      </CardContent>
    </Card>
  );
}
